import { HasIndex, bsConstants, validStr } from 'booksprout'
import { IsReviewerApp } from '../utils'
import { useLink } from '../modules/link/linkModule'

/**
 * Where the other three apps live, and how to get to them.
 *
 * The four apps are separate Quasar builds behind one domain (`/publisher`, `/reviewer`, `/admin` and
 * the root site), so moving between them is sometimes a router push and sometimes a document
 * navigation. `navigateToOtherApp` is the part that decides which.
 *
 * It needs the current route and the router, and whether this is the Capacitor build — the mobile app
 * has no other app to navigate to, so it always stays in the router. Those come in as arguments; the
 * mixin passes its own, and a `setup()` component passes what `useRouter()` gives it.
 */

export interface AppHostsContext {
  route: { query: HasIndex }
  router: { push (to: string): Promise<unknown> | void }
  isMobileApp: boolean
}

export function useAppHosts (context?: AppHostsContext) {
  const isReviewerApp = () => IsReviewerApp()

  const getAppHost = (appName = 'publisher', trailingSlash = true): string => {
    const testingMode = [process.env.ENVIRONMENT, process.env.BUILD_MODE].includes('testing') ? 'testing' : void 0
    const dev = {
      reviewer: 'https://dev.booksprout.co/reviewer',
      admin: 'https://dev.booksprout.co/admin',
      publisher: 'https://dev.booksprout.co/publisher',
      website: 'https://dev.booksprout.co'
    }

    const hosts: HasIndex = {
      development: dev,
      dev,
      testing: {
        reviewer: 'https://testing.booksprout.co/reviewer',
        admin: 'https://testing.booksprout.co/admin',
        publisher: 'https://testing.booksprout.co/publisher',
        website: 'https://testing.booksprout.co'
      },
      beta: {
        reviewer: 'https://beta.booksprout.co/reviewer',
        admin: 'https://beta.booksprout.co/admin',
        publisher: 'https://beta.booksprout.co/publisher',
        website: 'https://beta.booksprout.co'
      },
      sandbox: {
        reviewer: 'https://sandbox.booksprout.co/reviewer',
        admin: 'https://sandbox.booksprout.co/admin',
        publisher: 'https://sandbox.booksprout.co/publisher',
        website: 'https://sandbox.booksprout.co'
      },
      master: {
        reviewer: 'https://booksprout.co/reviewer',
        admin: 'https://booksprout.co/admin',
        publisher: 'https://booksprout.co/publisher',
        website: 'https://booksprout.co'
      }
    }

    const mode = testingMode || process.env.BUILD_MODE || process.env.NODE_ENV || 'master'
    return hosts[mode][appName] + (trailingSlash ? '/' : '')
  }

  const linkToOtherApp = (name: string, url: string): string => {
    if (name.startsWith('/')) {
      name = name.replace('/', '')
    }

    if (url.startsWith('/')) {
      url = url.replace('/', '')
    }

    const host = getAppHost(name)
    return host + (url || useLink().link().homeUrl)
  }

  const navigateToOtherApp = (url: string) => {
    if (context === void 0) {
      throw new Error('useAppHosts: navigateToOtherApp needs a route, a router and isMobileApp.')
    }

    const { route, router, isMobileApp } = context
    const apps = ['admin', 'publisher', 'reviewer']

    /**
     * Which site to redirect to. For local dev the localhost domain has to be worked out; live it
     * does not matter — forward to the url.
     *
     * `window.location`, which a browser gives the same object as `document.location`. jsdom does not,
     * and only lets `window`'s be replaced, so this is what lets a spec see where it was sent.
     */
    const navigateToCorrectApp = (goTo: string) => {
      const urlParts = window.location.href.split('/')
      const currentApp = urlParts[3]
      const goToApp = goTo.split('/')[0]
      let remainingGoToUrl = goTo.substring(goTo.indexOf('/'))
      if (goToApp === remainingGoToUrl) {
        remainingGoToUrl = ''
      }
      const homeUrl = goToApp ? (bsConstants.APP.HOME_URLS as HasIndex)[goToApp.toUpperCase()] : ''

      let isNavigatingToOtherApp = !url.startsWith(currentApp)
      // Root domain with no /publisher or /reviewer on it — work out whether this is the same domain.
      if (!isNavigatingToOtherApp && !currentApp) {
        isNavigatingToOtherApp = window.location.href + url !== getAppHost(goTo)
      }

      if (isNavigatingToOtherApp && !isMobileApp) {
        window.location.href = getAppHost(goToApp, false) + (remainingGoToUrl || homeUrl) +
          (route.query.forward ? '?forward=' + route.query.forward : '')
      } else {
        // Router accounts for the path already, so strip the publicPath part before pushing.
        let pushTo = goTo.substring(goTo.indexOf('/') + 1)
        apps.forEach(e => pushTo = pushTo.replace(e, ''))
        // Always direct to the root of the app before pushing.
        const target = validStr(pushTo) ? (pushTo.startsWith('/') ? pushTo : '/' + pushTo) : homeUrl
        const pushed = router.push(target)
        if (pushed && typeof (pushed as Promise<unknown>).catch === 'function') {
          void (pushed as Promise<unknown>).catch(() => {
            // do nothing, routeGuard redirected the visitor — this swallows the
            // "Uncaught (in promise) undefined" that a cancelled navigation throws
          })
        }
      }
    }

    navigateToCorrectApp(url)
  }

  return {
    isReviewerApp,
    getAppHost,
    linkToOtherApp,
    navigateToOtherApp
  }
}
